import { z } from 'zod';
import { listQuerySchema } from '../common/pagination';
import { moneySchema } from '../money';
import { projectCalcStatusSchema, projectGroupSchema } from './calculation.schema';

/**
 * Projekt = zentrale Klammer-Entität (CLAUDE.md §12). Datenmodell: ADR 0004.
 * Auf ein Projekt werden Stunden, Material, Kosten, Belege gebucht.
 */
export const projectStatusSchema = z.enum([
  'ANGELEGT', // angelegt
  'ENGINEERING', // Konstruktion/Auslegung
  'BESCHAFFUNG', // Material disponieren
  'FERTIGUNG', // Fertigung (sofern zutreffend)
  'MONTAGE', // Montage
  'IBN', // Inbetriebnahme
  'ABGENOMMEN', // vom Kunden abgenommen
  'GESCHLOSSEN', // abgeschlossen/archiviert
  'ON_HOLD', // pausiert
  'STORNIERT', // storniert
]);
export type ProjectStatus = z.infer<typeof projectStatusSchema>;

/**
 * Projekt-Ampel (Gesundheit), getrennt vom Status. Wird serverseitig AUTOMATISCH
 * aus dem Liefertermin ermittelt (überschritten = ROT, ≤ 14 Tage/ON_HOLD = GELB,
 * sonst GRÜN; abgeschlossen/storniert = GRÜN) — kein manuelles Feld mehr. Details
 * in apps/api/.../projects/project-health.ts.
 */
export const projectAmpelSchema = z.enum(['GRUEN', 'GELB', 'ROT']);
export type ProjectAmpel = z.infer<typeof projectAmpelSchema>;

/** Projektart im Anlagenbau (ADR 0004). */
export const projectTypeSchema = z.enum([
  'NEW_PLANT', // Neuanlage
  'CONVERSION', // Umbau/Erweiterung
  'MAINTENANCE', // Wartung
  'SERVICE', // Service/Reparatur
  'OTHER',
]);
export type ProjectType = z.infer<typeof projectTypeSchema>;

/**
 * Eingabe beim Anlegen eines Projekts. `code` ist optional (ADR 0045 §4): wird
 * er weggelassen, vergibt der Server automatisch die nächste Nummer aus dem
 * Projekt-Nummernkreis (`P-{Jahr}-{laufende Nummer}`); wird er angegeben, gilt
 * wie bisher nur die Eindeutigkeitsprüfung (rückwärtskompatibel).
 */
export const createProjectSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1)
    .max(32)
    .regex(/^[A-Za-z0-9._-]+$/, 'code darf nur Buchstaben, Ziffern, . _ - enthalten')
    .optional(),
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000).optional(),
  status: projectStatusSchema.default('ANGELEGT'),
  ampel: projectAmpelSchema.optional(),
  projectType: projectTypeSchema.default('NEW_PLANT'),
  /** Kunde aus dem CRM (Soft-Ref); der Name wird als Snapshot übernommen. */
  customerId: z.string().uuid().nullable().optional(),
  customerName: z.string().trim().max(200).optional(),
  /** Owner-Gesellschaft (ADR 0029): Default für Belege aus diesem Projekt. */
  ownerLegalEntityId: z.string().uuid().nullable().optional(),
  projectManager: z.string().trim().max(200).optional(),
  location: z.string().trim().max(200).optional(),
  /** Auftragswert als Money (Mikro-Einheiten als String + Währung, CLAUDE.md §6). */
  orderValue: moneySchema.optional(),
  startDate: z.string().datetime().optional(),
  plannedEndDate: z.string().datetime().optional(),
  deliveryDate: z.string().datetime().optional(),
  // PM-App Phase B (ADR 0051): erweiterte Stammdaten.
  /** Projektleitung als echte User (Mehrfachauswahl, Soft-Refs). */
  projectLeadUserIds: z.array(z.string().uuid()).max(10).optional(),
  country: z.string().trim().max(120).optional(),
  coordinates: z.string().trim().max(200).optional(),
  product: z.string().trim().max(200).optional(),
  plantType: z.string().trim().max(120).optional(),
  /** Ansprechpartner aus dem CRM (Soft-Ref); Name/Telefon werden serverseitig gesnapshottet. */
  contactId: z.string().uuid().nullable().optional(),
  /** Liefertermin-Zeitraum: deliveryDate = von, deliveryDateEnd = bis. */
  deliveryDateEnd: z.string().datetime().optional(),
  deliveryTimeNote: z.string().trim().max(200).optional(),
  /** Lieferadresse aus dem CRM (Soft-Ref); Adressblock wird serverseitig gesnapshottet. */
  deliveryAddressId: z.string().uuid().nullable().optional(),
  deliveryContactName: z.string().trim().max(200).optional(),
  incoterm: z.string().trim().max(10).optional(),
  incotermLocation: z.string().trim().max(200).optional(),
  packagingNote: z.string().trim().max(500).optional(),
  paymentTermsText: z.string().trim().max(1000).optional(),
  basinCount: z.number().int().min(0).max(1000).optional(),
  lengthM: z.number().min(0).max(100000).optional(),
  widthM: z.number().min(0).max(100000).optional(),
  heightM: z.number().min(0).max(100000).optional(),
  baseDesign: z.string().trim().max(500).optional(),
  projectLanguage: z.string().trim().max(10).optional(),
  certificates: z.string().trim().max(500).optional(),
  // Kalkulation (ADR 0055): Projektgruppe, WSP, GK-Sätze, Plan-VK, Kalk-Status, IC.
  projectGroup: projectGroupSchema.optional(),
  waterLevelM: z.number().min(0).max(100000).optional(),
  materialOverheadPct: z.number().min(0).max(1000).optional(),
  salesOverheadPct: z.number().min(0).max(1000).optional(),
  calcStatus: projectCalcStatusSchema.optional(),
  planSalesPrice: moneySchema.optional(),
  icTransferPrice: moneySchema.optional(),
  icProfitSplitPct: z.number().min(0).max(100).optional(),
  ownerSalesOverheadPct: z.number().min(0).max(1000).optional(),
});
export type CreateProjectInput = z.infer<typeof createProjectSchema>;

/**
 * Eingabe beim Aktualisieren — alle Felder optional (kein Default greift).
 * PATCH-Semantik der leerbaren Felder: `null` = Feld explizit leeren (DB null),
 * `undefined`/fehlend = unverändert lassen. Ohne `null` könnte der Dialog das
 * Leeren eines Feldes nicht übertragen (Wert bliebe still stehen).
 */
export const updateProjectSchema = createProjectSchema.partial().extend({
  description: z.string().trim().max(2000).nullable().optional(),
  projectManager: z.string().trim().max(200).nullable().optional(),
  location: z.string().trim().max(200).nullable().optional(),
  orderValue: moneySchema.nullable().optional(),
  startDate: z.string().datetime().nullable().optional(),
  plannedEndDate: z.string().datetime().nullable().optional(),
  deliveryDate: z.string().datetime().nullable().optional(),
  // Phase-B-Felder: null = leeren (PATCH-Semantik wie oben).
  country: z.string().trim().max(120).nullable().optional(),
  coordinates: z.string().trim().max(200).nullable().optional(),
  product: z.string().trim().max(200).nullable().optional(),
  plantType: z.string().trim().max(120).nullable().optional(),
  deliveryDateEnd: z.string().datetime().nullable().optional(),
  deliveryTimeNote: z.string().trim().max(200).nullable().optional(),
  deliveryContactName: z.string().trim().max(200).nullable().optional(),
  incoterm: z.string().trim().max(10).nullable().optional(),
  incotermLocation: z.string().trim().max(200).nullable().optional(),
  packagingNote: z.string().trim().max(500).nullable().optional(),
  paymentTermsText: z.string().trim().max(1000).nullable().optional(),
  basinCount: z.number().int().min(0).max(1000).nullable().optional(),
  lengthM: z.number().min(0).max(100000).nullable().optional(),
  widthM: z.number().min(0).max(100000).nullable().optional(),
  heightM: z.number().min(0).max(100000).nullable().optional(),
  baseDesign: z.string().trim().max(500).nullable().optional(),
  projectLanguage: z.string().trim().max(10).nullable().optional(),
  certificates: z.string().trim().max(500).nullable().optional(),
  // Kalkulation (ADR 0055): null = leeren; GK-/Split-Sätze sind NOT NULL (kein null).
  projectGroup: projectGroupSchema.nullable().optional(),
  waterLevelM: z.number().min(0).max(100000).nullable().optional(),
  planSalesPrice: moneySchema.nullable().optional(),
  icTransferPrice: moneySchema.nullable().optional(),
});
export type UpdateProjectInput = z.infer<typeof updateProjectSchema>;

/** Abnahme erfassen (Schritt 11): Datum (Default heute) + optionales Protokoll. */
export const recordAcceptanceSchema = z.object({
  acceptedAt: z.string().datetime().optional(),
  note: z.string().trim().max(5000).optional(),
});
export type RecordAcceptanceInput = z.infer<typeof recordAcceptanceSchema>;

/**
 * Listen-Query für Projekte. Ergänzt den Standard (§8) um `includeArchived`:
 * standardmäßig blenden wir soft-gelöschte (archivierte) Projekte aus (§7),
 * mit `includeArchived=true` erscheinen sie zusätzlich in der Liste.
 * Query-Parameter kommen als String an, daher als String-Enum modelliert.
 */
/**
 * Nachschlagewerk „Projektnummer → Name" für die Oberfläche: überall, wo nur die
 * Nummer gespeichert ist, steht damit auch der Name daneben (Tech-Lead 2026-07-28).
 * Bewusst winzig — die Liste wird auf vielen Seiten geladen und im Client gecacht.
 */
export const projectRefSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
});
export type ProjectRefResponse = z.infer<typeof projectRefSchema>;

export const projectListQuerySchema = listQuerySchema.extend({
  includeArchived: z.enum(['true', 'false']).optional(),
  // Referenzen-Ansicht (Vertrieb): zusätzliche Filter auf denselben Projektdaten.
  /** Land-Mehrfachauswahl, kommagetrennt (z. B. "AUT,GER,NLD"). */
  countries: z.string().trim().max(2000).optional(),
  plantType: z.string().trim().max(120).optional(),
  product: z.string().trim().max(200).optional(),
  projectGroup: projectGroupSchema.optional(),
  /** Baujahr-Zeitraum: Treffer, wenn startDate ODER deliveryDate in diesem Jahresbereich liegt. */
  yearFrom: z.coerce.number().int().min(1900).max(2100).optional(),
  yearTo: z.coerce.number().int().min(1900).max(2100).optional(),
  lengthMin: z.coerce.number().min(0).optional(),
  lengthMax: z.coerce.number().min(0).optional(),
  widthMin: z.coerce.number().min(0).optional(),
  widthMax: z.coerce.number().min(0).optional(),
  heightMin: z.coerce.number().min(0).optional(),
  heightMax: z.coerce.number().min(0).optional(),
});
export type ProjectListQuery = z.infer<typeof projectListQuerySchema>;

/**
 * Sprache eines Datei-Exports (DE/EN) — generisch, aktuell für den Referenzen-
 * PDF-Export genutzt (zweisprachig wie der Zeitplan-Export).
 */
export const exportLanguageSchema = z.enum(['de', 'en']);
export type ExportLanguage = z.infer<typeof exportLanguageSchema>;

/** Ein Filter-Facettenwert mit Trefferanzahl (z. B. Land „Deutschland" · 42). */
export const projectFacetSchema = z.object({ value: z.string(), count: z.number().int() });
export type ProjectFacet = z.infer<typeof projectFacetSchema>;

/** Verfügbare Filterwerte für die Referenzen-Ansicht, mit Anzahl. */
export const projectFacetsSchema = z.object({
  countries: z.array(projectFacetSchema),
  plantTypes: z.array(projectFacetSchema),
  products: z.array(projectFacetSchema),
});
export type ProjectFacets = z.infer<typeof projectFacetsSchema>;

/**
 * Nachträgliche Anreicherung bestehender Projekte mit historischen
 * Referenzdaten (Base44-Migration): `code` ist der Match-Schlüssel, alle
 * anderen Felder sind optional und werden nur bei Angabe überschrieben
 * (PATCH-Semantik über `updateProject`, siehe `ProjectsService.importReferenceData`).
 */
export const importProjectReferenceRowSchema = z.object({
  code: z.string().trim().min(1).max(32),
  country: z.string().trim().max(120).optional(),
  coordinates: z.string().trim().max(200).optional(),
  product: z.string().trim().max(200).optional(),
  plantType: z.string().trim().max(120).optional(),
  basinCount: z.number().int().min(0).max(1000).optional(),
  lengthM: z.number().min(0).max(100000).optional(),
  widthM: z.number().min(0).max(100000).optional(),
  heightM: z.number().min(0).max(100000).optional(),
  projectGroup: projectGroupSchema.optional(),
  startDate: z.string().datetime().optional(),
  deliveryDate: z.string().datetime().optional(),
});
export type ImportProjectReferenceRow = z.infer<typeof importProjectReferenceRowSchema>;

export const importProjectReferenceDataSchema = z.object({
  rows: z.array(importProjectReferenceRowSchema).min(1).max(5000),
});
export type ImportProjectReferenceDataInput = z.infer<typeof importProjectReferenceDataSchema>;

/**
 * Auswahl für Sammel-Aktionen (Bulk): entweder explizite `ids` (aktuelle Auswahl)
 * ODER `allMatching: true` mit den Listen-Filtern (dann wirkt die Aktion auf ALLE
 * zum Filter passenden Projekte, auch über Seitengrenzen hinweg). Die IDs werden
 * serverseitig aus dem Filter aufgelöst — der Client schickt bei „alle" keine
 * (potenziell tausend) IDs mit.
 */
const bulkSelectionFields = {
  ids: z.array(z.string().uuid()).max(5000).optional(),
  allMatching: z.boolean().optional(),
  q: z.string().trim().max(200).optional(),
  customerId: z.string().uuid().optional(),
  includeArchived: z.boolean().optional(),
};

/** Mindestens eine Auswahlquelle (IDs oder „alle passenden") muss gesetzt sein. */
function hasSelection(sel: { ids?: string[]; allMatching?: boolean }): boolean {
  return sel.allMatching === true || (Array.isArray(sel.ids) && sel.ids.length > 0);
}

/** Sammel-Statuswechsel: Status mehrerer Projekte gemeinsam setzen. */
export const bulkUpdateProjectStatusSchema = z
  .object({ ...bulkSelectionFields, status: projectStatusSchema })
  .refine(hasSelection, { message: 'Es muss mindestens ein Projekt ausgewählt sein.' });
export type BulkUpdateProjectStatusInput = z.infer<typeof bulkUpdateProjectStatusSchema>;

/**
 * Sammel-Archivierung. `force` (nur Admins) archiviert auch, wenn noch nicht
 * stornierte Belege / offene Posten am Projekt hängen — für Altbestand.
 */
export const bulkArchiveProjectsSchema = z
  .object({ ...bulkSelectionFields, force: z.boolean().optional() })
  .refine(hasSelection, { message: 'Es muss mindestens ein Projekt ausgewählt sein.' });
export type BulkArchiveProjectsInput = z.infer<typeof bulkArchiveProjectsSchema>;

/** Ergebnis einer Sammel-Aktion: wie viele gelangen, welche scheiterten (mit Grund). */
export const bulkResultSchema = z.object({
  requested: z.number().int(),
  succeeded: z.number().int(),
  failed: z.array(
    z.object({
      id: z.string().uuid(),
      code: z.string().optional(),
      reason: z.string(),
    }),
  ),
});
export type BulkResult = z.infer<typeof bulkResultSchema>;

/** Antwort-Form eines Projekts über die API. */
export const projectResponseSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  status: projectStatusSchema,
  ampel: projectAmpelSchema,
  projectType: projectTypeSchema,
  customerId: z.string().uuid().nullable(),
  customerName: z.string().nullable(),
  ownerLegalEntityId: z.string().uuid().nullable(),
  ownerLegalEntityName: z.string().nullable(),
  projectManager: z.string().nullable(),
  location: z.string().nullable(),
  orderValue: moneySchema.nullable(),
  startDate: z.string().datetime().nullable(),
  plannedEndDate: z.string().datetime().nullable(),
  deliveryDate: z.string().datetime().nullable(),
  /** Abnahme (Schritt 11): null = noch nicht abgenommen. */
  acceptedAt: z.string().datetime().nullable(),
  acceptanceNote: z.string().nullable(),
  // PM-App Phase B (ADR 0051).
  projectLeadUserIds: z.array(z.string().uuid()),
  country: z.string().nullable(),
  coordinates: z.string().nullable(),
  product: z.string().nullable(),
  plantType: z.string().nullable(),
  contactId: z.string().uuid().nullable(),
  contactName: z.string().nullable(),
  contactPhone: z.string().nullable(),
  deliveryDateEnd: z.string().datetime().nullable(),
  deliveryTimeNote: z.string().nullable(),
  deliveryAddressId: z.string().uuid().nullable(),
  deliveryAddressText: z.string().nullable(),
  deliveryContactName: z.string().nullable(),
  incoterm: z.string().nullable(),
  incotermLocation: z.string().nullable(),
  packagingNote: z.string().nullable(),
  paymentTermsText: z.string().nullable(),
  basinCount: z.number().int().nullable(),
  lengthM: z.number().nullable(),
  widthM: z.number().nullable(),
  heightM: z.number().nullable(),
  baseDesign: z.string().nullable(),
  projectLanguage: z.string().nullable(),
  certificates: z.string().nullable(),
  // Kalkulation (ADR 0055). Finanz-/Kalkulationsfelder werden für Nutzer ohne
  // `projects.viewfinancials` serverseitig auf null gestrippt (ADR 0062,
  // stripProjectFinancials) — daher durchgängig nullable, auch die Prozentsätze.
  projectGroup: projectGroupSchema.nullable(),
  waterLevelM: z.number().nullable(),
  materialOverheadPct: z.number().nullable(),
  salesOverheadPct: z.number().nullable(),
  calcStatus: projectCalcStatusSchema,
  planSalesPrice: moneySchema.nullable(),
  icTransferPrice: moneySchema.nullable(),
  icProfitSplitPct: z.number().nullable(),
  ownerSalesOverheadPct: z.number().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  /** Archiviert (Soft-Delete, §7): null = aktiv, sonst Zeitpunkt der Archivierung. */
  deletedAt: z.string().datetime().nullable(),
});
export type ProjectResponse = z.infer<typeof projectResponseSchema>;
